<template>
  <div>
    <!-- Linha 1: tipo de relatório + exportar CSV -->
    <v-row class="filters--list mb-2" align="center">
      <v-col cols="12" sm="6" md="4">
        <v-select
          v-model="selectedType"
          :items="financialReportTypes"
          item-text="label"
          item-value="type"
          label="Tipo de relatório"
          outlined
          dense
          hide-details
          clearable
          @change="onTypeChange"
        />
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <v-btn
          color="primary"
          elevation="0"
          :disabled="!filteredRows.length"
          @click="exportCsv"
        >
          <v-icon left>mdi-file-delimited-outline</v-icon>
          Exportar CSV
        </v-btn>
      </v-col>
    </v-row>

    <!-- Linha 2: filtros client-side (aparecem quando um tipo é selecionado) -->
    <v-row v-if="selectedType" class="filters--list mb-4">
      <v-col v-if="dateColumn" cols="12" sm="12" md="6" lg="3">
        <financial-report-date-filter
          ref="dateFilter"
          @change="onDateFilterChange"
        />
      </v-col>

      <v-col v-if="partnerOptions.length" cols="12" sm="12" md="6" lg="3">
        <v-autocomplete
          v-model="filterPartner"
          :items="partnerOptions"
          label="Parceiro"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          single-line
          clearable
          hide-no-data
        />
      </v-col>

      <v-col cols="12" sm="12" md="6" lg="3">
        <v-autocomplete
          v-model="filterCustomer"
          :search-input.sync="customerSearch"
          :items="customerOptions"
          :label="$t('genericFilters.label.customer')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          single-line
          clearable
          hide-no-data
        />
      </v-col>

      <v-col v-if="subscriptionOptions.length" cols="12" sm="12" md="6" lg="3">
        <v-select
          v-model="filterSubscription"
          :items="subscriptionOptions"
          item-text="label"
          item-value="value"
          label="Assinatura"
          outlined
          dense
          single-line
          clearable
        />
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        mobile-breakpoint="20"
        :headers="tableHeaders"
        :items="displayRows"
        :loading="isFinancialReportLoading"
        :no-data-text="noDataText"
        loading-text="Carregando..."
        hide-default-footer
        disable-sort
        :items-per-page="-1"
      />
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import FinancialReportDateFilter from './components/FinancialReportDateFilter'

const MONTH_PT = {
  January: 'Janeiro', February: 'Fevereiro', March: 'Março', April: 'Abril',
  May: 'Maio', June: 'Junho', July: 'Julho', August: 'Agosto',
  September: 'Setembro', October: 'Outubro', November: 'Novembro', December: 'Dezembro',
}

const CUSTOMER_COLUMN = {
  NEGATIVE_HOSTS: 'customer',
  NEGATIVE_BALANCE_PROPERTIES: 'customer',
  INVOICES_AGAINST_HAUSEFUL: 'customer',
  MONTHLY_STATEMENT_HISTORY: 'customer',
  EXTRA_CONSUMPTION_PER_CONTRACT: 'real_estate_name',
  CONTRACTS_DUE_DATE: 'customer',
}

const SUBSCRIPTION_COLUMN = {
  NEGATIVE_BALANCE_PROPERTIES: 'subscription_type',
  EXTRA_CONSUMPTION_PER_CONTRACT: 'contract_subscription_type',
  CONTRACTS_DUE_DATE: 'contract_type',
}

const PARTNER_COLUMN = {
  INVOICES_AGAINST_HAUSEFUL: 'partner',
}

const DEFAULT_START_DATE = '2000-01-01'

const DATE_COLUMN = {
  NEGATIVE_HOSTS: 'max_visit_date',
  NEGATIVE_BALANCE_PROPERTIES: 'max_created_at',
  MONTHLY_STATEMENT_HISTORY: 'lastMovement',
  INVOICES_AGAINST_HAUSEFUL: 'transferDate',
  EXTRA_CONSUMPTION_PER_CONTRACT: 'max_visit_date_scheduled',
  CONTRACTS_DUE_DATE: 'end_date',
}

// notas emitidas pela Hauseful não têm transferência: vale a data da própria nota
const DATE_FALLBACK_COLUMN = {
  INVOICES_AGAINST_HAUSEFUL: 'invoiceDate',
}

export default {
  name: 'FinancialReports',

  components: { FinancialReportDateFilter },

  data() {
    return {
      selectedType: null,

      filterCustomer: null,
      customerSearch: null,
      filterSubscription: null,
      filterPartner: null,
      filterDateStart: null,
      filterDateEnd: null,
    }
  },

  watch: {
    filterPartner() {
      // ao trocar o parceiro, limpa o cliente para não manter seleção de outro parceiro
      this.filterCustomer = null
      this.customerSearch = null
    },
  },

  computed: {
    ...mapGetters([
      'financialReportTypes',
      'financialReportRows',
      'isFinancialReportLoading',
    ]),

    selectedTypeObj() {
      return this.financialReportTypes.find((t) => t.type === this.selectedType) || null
    },

    requiresDate() {
      return this.selectedTypeObj?.requires_date ?? false
    },

    customerColumn() {
      return this.selectedType ? CUSTOMER_COLUMN[this.selectedType] : null
    },

    subscriptionColumn() {
      return this.selectedType ? SUBSCRIPTION_COLUMN[this.selectedType] : null
    },

    partnerColumn() {
      return this.selectedType ? PARTNER_COLUMN[this.selectedType] : null
    },

    dateColumn() {
      return this.selectedType ? DATE_COLUMN[this.selectedType] : null
    },

    dateFallbackColumn() {
      return this.selectedType ? DATE_FALLBACK_COLUMN[this.selectedType] : null
    },

    customerOptions() {
      if (!this.customerColumn || !this.financialReportRows.length) return []
      let rows = this.financialReportRows
      if (this.filterPartner && this.partnerColumn) {
        rows = rows.filter((r) => {
          const val = r[this.partnerColumn]
          return val !== null && val !== undefined && String(val).trim() === this.filterPartner
        })
      }
      const values = rows
        .map((r) => r[this.customerColumn])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v).trim())
      return [...new Set(values)].sort()
    },

    subscriptionOptions() {
      if (!this.subscriptionColumn || !this.financialReportRows.length) return []
      const values = this.financialReportRows
        .map((r) => r[this.subscriptionColumn])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v).trim())
      return [...new Set(values)].sort().map((v) => ({
        value: v,
        label: this.translateSubscription(v),
      }))
    },

    partnerOptions() {
      if (!this.partnerColumn || !this.financialReportRows.length) return []
      const values = this.financialReportRows
        .map((r) => r[this.partnerColumn])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v).trim())
      return [...new Set(values)].sort()
    },

    filteredRows() {
      let rows = this.financialReportRows

      const customerTerm = (this.filterCustomer || this.customerSearch || '').trim()
      if (customerTerm && this.customerColumn) {
        const search = customerTerm.toLowerCase()
        rows = rows.filter((r) => {
          const val = r[this.customerColumn]
          return val !== null && val !== undefined &&
            String(val).trim().toLowerCase().includes(search)
        })
      }

      if (this.filterSubscription && this.subscriptionColumn) {
        rows = rows.filter((r) => {
          const val = r[this.subscriptionColumn]
          return val !== null && val !== undefined && String(val).trim() === this.filterSubscription
        })
      }

      if (this.filterPartner && this.partnerColumn) {
        rows = rows.filter((r) => {
          const val = r[this.partnerColumn]
          return val !== null && val !== undefined && String(val).trim() === this.filterPartner
        })
      }

      if (this.filterDateStart && this.dateColumn) {
        rows = rows.filter((r) => {
          const val = this.rowDate(r)
          if (!val) return false
          return moment(val).isSameOrAfter(this.filterDateStart, 'day')
        })
      }

      if (this.filterDateEnd && this.dateColumn) {
        rows = rows.filter((r) => {
          const val = this.rowDate(r)
          if (!val) return false
          return moment(val).isSameOrBefore(this.filterDateEnd, 'day')
        })
      }

      return rows
    },

    displayRows() {
      return this.filteredRows.map((row) => {
        const formatted = {}
        Object.keys(row).forEach((key) => {
          formatted[key] = this.formatCell(row[key], key)
        })
        return formatted
      })
    },

    tableHeaders() {
      if (!this.financialReportRows.length) return []
      return Object.keys(this.financialReportRows[0]).map((key) => ({
        text: this.$t(`financialReport.columns.${key}`) || key,
        value: key,
      }))
    },

    noDataText() {
      return this.selectedType
        ? 'Nenhum resultado encontrado'
        : 'Selecione um tipo de relatório'
    },
  },

  methods: {
    ...mapActions(['fetchFinancialReportTypes', 'executeFinancialReport']),

    onTypeChange(type) {
      this.resetLocalFilters()
      if (type) {
        this.execute()
      } else {
        this.$store.commit('setFinancialReportRows', [])
      }
    },

    execute() {
      // busca tudo desde sempre quando o relatório exige data — o filtro de datas
      // na tela é só um refinamento local, igual cliente/parceiro/assinatura,
      // sem depender de ordem de seleção
      const date = this.requiresDate ? DEFAULT_START_DATE : null
      this.executeFinancialReport({ type: this.selectedType, date })
    },

    rowDate(row) {
      return row[this.dateColumn] || (this.dateFallbackColumn ? row[this.dateFallbackColumn] : null)
    },

    onDateFilterChange({ start, end }) {
      this.filterDateStart = start
      this.filterDateEnd = end
    },

    resetLocalFilters() {
      this.filterCustomer = null
      this.customerSearch = null
      this.filterSubscription = null
      this.filterPartner = null
      this.filterDateStart = null
      this.filterDateEnd = null
      this.$nextTick(() => {
        if (this.$refs.dateFilter) this.$refs.dateFilter.clearAll()
      })
    },

    translateSubscription(value) {
      if (!value) return '---'
      const key = `realEstate.subscriptionTypes.${value}`
      const translated = this.$t(key)
      return translated !== key ? translated : value
    },

    formatCell(value, columnKey) {
      if (value === null || value === undefined) return '---'
      if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
      if (columnKey === this.subscriptionColumn) return this.translateSubscription(value)
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const m = moment(value)
        return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : value
      }
      if (columnKey === 'month' && typeof value === 'string') {
        return MONTH_PT[value] || value
      }
      return value
    },

    exportCsv() {
      if (!this.filteredRows.length) return

      const headers = Object.keys(this.filteredRows[0])
      const translatedHeaders = headers.map(
        (h) => this.$t(`financialReport.columns.${h}`) || h
      )
      const rows = this.filteredRows.map((row) =>
        headers.map((h) => {
          const val = row[h]
          if (val === null || val === undefined) return ''
          const str = String(val)
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str
        }).join(',')
      )

      const csv = [translatedHeaders.join(','), ...rows].join('\n')
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const filename = this.selectedTypeObj?.label ?? 'relatorio'
      link.href = url
      link.setAttribute('download', `${filename}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  },

  mounted() {
    this.fetchFinancialReportTypes()
  },
}
</script>
